import apiClient from '../apiClient';
import { API_ENDPOINTS } from '../config';

const locationService = {
  // Get all locations with pagination and filters
  getLocations: async (params = {}) => {
    try {


      const url = API_ENDPOINTS.LOCATIONS.BASE;


      const response = await apiClient.get(url, { params });


      // Return the full response structure with success field
      return {
        success: true,
        data: response.data,
        message: 'Locations fetched successfully'
      };
    } catch (error) {


      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch locations',
        data: null
      };
    }
  },

  // Get active locations for dropdowns
  getActiveLocations: async () => {
    try {


      const url = API_ENDPOINTS.LOCATIONS.ACTIVE;


      const response = await apiClient.get(url);


      return {
        success: true,
        data: response.data,
        message: 'Active locations fetched successfully'
      };
    } catch (error) {


      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch active locations',
        data: []
      };
    }
  },

  // Get location by ID
  getLocationById: async (id) => {
    try {


      const url = `${API_ENDPOINTS.LOCATIONS.BASE}/${id}`;


      const response = await apiClient.get(url);


      return {
        success: true,
        data: response.data,
        message: 'Location fetched successfully'
      };
    } catch (error) {


      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch location',
        data: null
      };
    }
  },

  // Create new location
  createLocation: async (locationData) => {
    try {


      const url = API_ENDPOINTS.LOCATIONS.BASE;


      const response = await apiClient.post(url, locationData);


      return {
        success: true,
        data: response.data,
        message: 'Location created successfully'
      };
    } catch (error) {


      // Handle different error formats
      let errorMessage = 'Failed to create location';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.responseMessage) {
        errorMessage = error.response.data.responseMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        message: errorMessage,
        data: null
      };
    }
  },

  // Update location
  updateLocation: async (id, locationData) => {
    try {


      const url = `${API_ENDPOINTS.LOCATIONS.BASE}/${id}`;


      const response = await apiClient.put(url, locationData);


      return {
        success: true,
        data: response.data,
        message: 'Location updated successfully'
      };
    } catch (error) {


      // Handle different error formats
      let errorMessage = 'Failed to update location';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.responseMessage) {
        errorMessage = error.response.data.responseMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        message: errorMessage,
        data: null
      };
    }
  },

  // Delete location (soft delete)
  deleteLocation: async (id) => {
    try {


      const url = `${API_ENDPOINTS.LOCATIONS.BASE}/${id}`;


      const response = await apiClient.delete(url);


      return {
        success: true,
        data: response.data,
        message: 'Location deleted successfully'
      };
    } catch (error) {


      // Handle different error formats
      let errorMessage = 'Failed to delete location';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.responseMessage) {
        errorMessage = error.response.data.responseMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        message: errorMessage,
        data: null
      };
    }
  },

  // Bulk delete locations
  bulkDeleteLocations: async (ids) => {
    try {

      const url = `${API_ENDPOINTS.LOCATIONS.BASE}/bulk-delete`;
      const response = await apiClient.post(url, { ids });
      return {
        success: true,
        data: response.data,
        message: 'Locations deleted successfully'
      };
    } catch (error) {

      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to delete locations',
        data: null
      };
    }
  },

  // Bulk create locations
  bulkCreateLocations: async (locationsData) => {
    try {


      const url = `${API_ENDPOINTS.LOCATIONS.BASE}/bulk-create`;


      const response = await apiClient.post(url, { locations: locationsData });


      return {
        success: true,
        data: response.data,
        message: 'Locations created successfully'
      };
    } catch (error) {


      // Handle different error formats
      let errorMessage = 'Failed to bulk create locations';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.responseMessage) {
        errorMessage = error.response.data.responseMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        message: errorMessage,
        data: null
      };
    }
  },

  // Helper function to transform locations for dropdown format
  transformForDropdown: (locations) => {
    if (!Array.isArray(locations)) {

      return [];
    }

    return locations.map(location => {
      if (typeof location === 'string') {
        return {
          value: location,
          label: location,
          code: location.toUpperCase().substring(0, 3),
          type: 'Office'
        };
      } else if (location && typeof location === 'object') {
        return {
          value: location.name || location.label || location.value,
          label: location.name || location.label || location.value,
          code: location.code || '',
          type: location.type || 'Office',
          city: location.city || location.address?.city || '',
          state: location.state || location.address?.state || ''
        };
      }
      return null;
    }).filter(Boolean);
  },

  // Helper function to get location options for forms
  getLocationOptions: async () => {
    try {
      const result = await locationService.getActiveLocations();

      if (result.success) {
        return locationService.transformForDropdown(result.data);
      } else {

        return [];
      }
    } catch (error) {

      // Return fallback options if API fails
      return [
        { value: 'Mumbai', label: 'Mumbai', code: 'MUM', type: 'Office' },
        { value: 'Delhi', label: 'Delhi', code: 'DEL', type: 'Office' },
        { value: 'Bangalore', label: 'Bangalore', code: 'BLR', type: 'Office' },
        { value: 'Chennai', label: 'Chennai', code: 'CHE', type: 'Office' },
        { value: 'Remote', label: 'Remote', code: 'REM', type: 'Remote' }
      ];
    }
  }
};

export default locationService;
